import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { combineLatest, map, Observable, of, switchMap, take } from 'rxjs';
import { environment } from '../environment';
import {
  BETALNINGSUPPGIFT_REMINDER_PENDING_KEY,
  BEDOMARE_REMINDER_PENDING_KEY,
} from '../session-storage-keys';
import { AuthCookieService } from '../services/auth-cookie.service';
import { AuthService } from '../services/auth.service';

export const AuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> => {
  const authService = inject(AuthService);
  const authCookieService = inject(AuthCookieService);
  const env = inject(environment);

  if (!env.b2cMsalConfig?.authority || !env.b2cMsalConfig?.scope) {
    throw new Error('B2C auth or scopes is missing');
  }

  const cookie = authCookieService.getCookie();
  const rapportId = route.paramMap.get('id');
  const rapportForhandsgranskning = isRapportForhandsgranskning(rapportId);

  return combineLatest([
    authService.hasValidAccount(),
    authService.hasValidAccountAndActiveCookie(),
  ]).pipe(
    take(1),
    switchMap(([hasValidAccount, hasValidAccountAndActiveCookie]) => {
      if (hasValidAccountAndActiveCookie) {
        return of(true);
      } else if (cookie) {
        return authService
          .login(state.url, env.b2cMsalConfig.authority + cookie.tfp, [
            env.b2cMsalConfig.scope,
          ])
          .pipe(map(() => false));
      } else if (!cookie && hasValidAccount) {
        return authService.logout().pipe(map(() => false));
      } else if (isEffektaSharedView() || rapportForhandsgranskning) {
        return authService
          .login(state.url, env.b2cMsalConfig.policies.sso.authority, [
            env.b2cMsalConfig.scope,
          ])
          .pipe(map(() => false));
      } else if (env.name === 'Local') {
        return authService
          .login(state.url, env.b2cMsalConfig.policies.signIn.authority, [
            env.b2cMsalConfig.scope,
          ])
          .pipe(map(() => false));
      } else {
        sessionStorage.setItem(BETALNINGSUPPGIFT_REMINDER_PENDING_KEY, 'true');
        sessionStorage.setItem(BEDOMARE_REMINDER_PENDING_KEY, 'true');
        globalThis.location.replace(
          `${env.etjansterCommonUrl}/public/login?returnUrl=${encodeURIComponent(
            location.href,
          )}`,
        );
        return of(false);
      }
    }),
  );
};

function isEffektaSharedView() {
  const regex =
    /^https?:\/\/[^/]+\/projekt\/(budget|inrapporterade-kostnader)\/\d{4}-\d{5}(\/(write|read))?\/effekta(\/[^/]+)?$/;
  return regex.test(location.href);
}

function isRapportForhandsgranskning(_id: string): boolean {
  return /^https?:\/\/[^/]+\/rapport\/\d+\/forhandsgranskning$/.test(
    location.href,
  );
}
